/**
 * Shared source-of-truth constants for the Trident AI Enablement Platform frontend.
 *
 * This file MIRRORS the backend `apps/api/src/core/constants.py`. The backend is the
 * authoritative owner of these values (sensitivity tiers, Entra group names, demo
 * personas); this file exists so the React app and all frontend tasks import from a
 * single place and NEVER hardcode group names, tier strings, or persona keys inline.
 *
 * If the backend constants change, update this file to match. Keep the literal string
 * values identical to the backend so the deterministic permission filter
 * (`allowed_groups ∩ user.entra_groups`) behaves consistently across the wire.
 *
 * References:
 *   - docs/tech_spec.md §5.1 (retrieval chunk schema, SENSITIVITY enum, allowed_groups)
 *   - docs/goals.md §4 (user personas) and §3 (use cases)
 */

// ---------------------------------------------------------------------------
// Sensitivity tiers
// ---------------------------------------------------------------------------

/**
 * Document sensitivity classifications. Ordered most-restrictive → least.
 * Mirrors `SENSITIVITY` in the backend (tech_spec.md §5.1).
 */
export const SENSITIVITY_TIERS = [
  'ITAR',
  'CUI',
  'PHI',
  'comp',
  'review',
  'none',
] as const;

export type SensitivityTier = (typeof SENSITIVITY_TIERS)[number];

/** Human-readable labels for each sensitivity tier (UI badges, tooltips). */
export const SENSITIVITY_LABELS: Record<SensitivityTier, string> = {
  ITAR: 'ITAR — Export Controlled',
  CUI: 'CUI — Controlled Unclassified',
  PHI: 'PHI — Protected Health Info',
  comp: 'Compensation',
  review: 'Performance Review',
  none: 'Unlabelled — the source carries no classification label',
};

/** Tailwind badge color tokens per tier (kept here so badges never hardcode). */
export const SENSITIVITY_BADGE_CLASSES: Record<SensitivityTier, string> = {
  ITAR: 'bg-red-100 text-red-800',
  CUI: 'bg-amber-100 text-amber-800',
  PHI: 'bg-purple-100 text-purple-800',
  comp: 'bg-pink-100 text-pink-800',
  review: 'bg-indigo-100 text-indigo-800',
  none: 'bg-steel-100 text-steel-700',
};

// ---------------------------------------------------------------------------
// Entra ID groups
// ---------------------------------------------------------------------------

/**
 * Entra ID group names used as the authorization primitive for the deterministic
 * permission filter. These `entra-grp-*` strings are inherited onto document
 * `allowed_groups` at ingest and intersected with a user's `entra_groups` at query
 * time (tech_spec.md §5.1, §6).
 */
export const ENTRA_GROUPS = {
  ENGINEERING_ITAR: 'entra-grp-engineering-itar',
  PROGRAM_X: 'entra-grp-program-x',
  FINANCE: 'entra-grp-finance',
  HR: 'entra-grp-hr',
  BUSINESS_DEV: 'entra-grp-bd',
  SUPPLY_CHAIN: 'entra-grp-supply-chain',
  LEADERSHIP: 'entra-grp-leadership',
  GENERAL: 'entra-grp-general',
} as const;

export type EntraGroupKey = keyof typeof ENTRA_GROUPS;
export type EntraGroup = (typeof ENTRA_GROUPS)[EntraGroupKey];

/** All Entra group names as a flat, iterable list. */
export const ENTRA_GROUP_NAMES: readonly EntraGroup[] = Object.values(ENTRA_GROUPS);

/** Human-readable labels for each Entra group. */
export const ENTRA_GROUP_LABELS: Record<EntraGroup, string> = {
  [ENTRA_GROUPS.ENGINEERING_ITAR]: 'Engineering (ITAR)',
  [ENTRA_GROUPS.PROGRAM_X]: 'Program X',
  [ENTRA_GROUPS.FINANCE]: 'Finance',
  [ENTRA_GROUPS.HR]: 'HR / Talent',
  [ENTRA_GROUPS.BUSINESS_DEV]: 'Business Development',
  [ENTRA_GROUPS.SUPPLY_CHAIN]: 'Supply Chain',
  [ENTRA_GROUPS.LEADERSHIP]: 'Leadership',
  [ENTRA_GROUPS.GENERAL]: 'General Workforce',
};

// ---------------------------------------------------------------------------
// Demo personas
// ---------------------------------------------------------------------------

/**
 * Demo personas for the mock-mode persona switcher. Each persona carries a set of
 * Entra group claims; switching personas live demonstrates how the deterministic
 * permission filter returns different results for the same query.
 *
 * Persona KEYS are the stable identifiers used on the wire and by frontend tasks;
 * never hardcode them. Mirrors the backend persona registry (core.constants).
 */
export const PERSONA_KEYS = [
  'engineer_itar',
  'finance_analyst',
  'hr_specialist',
  'bd_manager',
  'supply_chain',
  'executive',
] as const;

export type PersonaKey = (typeof PERSONA_KEYS)[number];

export interface Persona {
  /** Stable key used on the wire and for persona switching. */
  key: PersonaKey;
  /** Display name shown in the persona switcher / header. */
  displayName: string;
  /** Email the mock identity layer issues for this persona. */
  email: string;
  /** Job title / role descriptor. */
  title: string;
  /** Entra group claims granted to this persona. */
  groups: readonly EntraGroup[];
}

/** Persona registry keyed by {@link PersonaKey}. */
export const PERSONAS: Record<PersonaKey, Persona> = {
  engineer_itar: {
    key: 'engineer_itar',
    displayName: 'Dana Ross',
    email: 'dana.ross@tridsys.com',
    title: 'Defense Software Engineer (ITAR-cleared)',
    groups: [
      ENTRA_GROUPS.ENGINEERING_ITAR,
      ENTRA_GROUPS.PROGRAM_X,
      ENTRA_GROUPS.GENERAL,
    ],
  },
  finance_analyst: {
    key: 'finance_analyst',
    displayName: 'Morgan Lee',
    email: 'morgan.lee@tridsys.com',
    title: 'Finance Analyst',
    groups: [ENTRA_GROUPS.FINANCE, ENTRA_GROUPS.GENERAL],
  },
  hr_specialist: {
    key: 'hr_specialist',
    displayName: 'Priya Shah',
    email: 'priya.shah@tridsys.com',
    title: 'HR / Talent Specialist',
    groups: [ENTRA_GROUPS.HR, ENTRA_GROUPS.GENERAL],
  },
  bd_manager: {
    key: 'bd_manager',
    displayName: 'Chris Vance',
    email: 'chris.vance@tridsys.com',
    title: 'Business Development Manager',
    groups: [ENTRA_GROUPS.BUSINESS_DEV, ENTRA_GROUPS.GENERAL],
  },
  supply_chain: {
    key: 'supply_chain',
    displayName: 'Sam Carter',
    email: 'sam.carter@tridsys.com',
    title: 'Supply Chain Coordinator',
    groups: [ENTRA_GROUPS.SUPPLY_CHAIN, ENTRA_GROUPS.GENERAL],
  },
  executive: {
    key: 'executive',
    displayName: 'Alex Trident',
    email: 'alex.trident@tridsys.com',
    title: 'Executive Leadership',
    groups: [
      ENTRA_GROUPS.LEADERSHIP,
      ENTRA_GROUPS.FINANCE,
      ENTRA_GROUPS.BUSINESS_DEV,
      ENTRA_GROUPS.GENERAL,
    ],
  },
};

/** Personas as an ordered list for rendering (switcher, login page). */
export const PERSONA_LIST: readonly Persona[] = PERSONA_KEYS.map((k) => PERSONAS[k]);

// ---------------------------------------------------------------------------
// User roles
// ---------------------------------------------------------------------------

/** App-level roles (distinct from Entra groups; govern admin/console access). */
export const USER_ROLES = ['admin', 'power_user', 'analyst', 'viewer'] as const;
export type UserRole = (typeof USER_ROLES)[number];

// ---------------------------------------------------------------------------
// Run / agent execution statuses (P3)
// ---------------------------------------------------------------------------

/** Agent/DAG run lifecycle statuses (mirrors backend RunStatus). */
export const RUN_STATUSES = [
  'pending',
  'running',
  'completed',
  'failed',
  'cancelled',
] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];
